import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Layout } from "@/components/Layout";
import { useAuth } from "@/contexts/AuthContext";
import { api, formatApiError, COUNTRY_FLAGS } from "@/lib/api";
import {
  Users, Home, Eye, Rocket, ShieldAlert, ShieldCheck, Sliders, BarChart3,
  TrendingUp, UserX, Clock, Plus, AlertCircle, Wallet, ArrowRight,
} from "lucide-react";
import { toast } from "sonner";

const fmtDate = (iso) => {
  if (!iso) return "";
  try { return new Date(iso).toLocaleString("fr-FR", { dateStyle: "short", timeStyle: "short" }); }
  catch { return ""; }
};

const REASON_LABELS = {
  spam: "Spam", fraud: "Fraude", duplicate: "Doublon",
  inappropriate: "Inapproprié", wrong_info: "Infos erronées", other: "Autre",
};

export default function AdminHome() {
  const { user, loading } = useAuth();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (loading) return;
    if (!user) return navigate("/login");
    if (user.role !== "admin") return navigate("/dashboard");
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get("/admin/overview");
        if (!cancelled) setData(data);
      } catch (e) {
        if (cancelled) return;
        const msg = formatApiError(e);
        setError(msg);
        toast.error(msg);
      }
    })();
    return () => { cancelled = true; };
  }, [user, loading, navigate]);

  if (!user || user.role !== "admin") return null;

  if (!data) {
    return (
      <Layout withFooter={false}>
        <div className="max-w-7xl mx-auto px-6 lg:px-8 py-8" data-testid="admin-home-loading">
          <div className="h-12 w-96 rounded-md bg-[#F0EBE6] animate-pulse mb-8" />
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
            {[0,1,2,3].map(i => <div key={i} className="h-32 rounded-2xl bg-[#F5F0EB] animate-pulse" />)}
          </div>
          {error && (
            <div className="rounded-2xl border border-[#D6202C]/30 bg-[#FEF2F3] text-[#D6202C] p-4 text-sm">
              {error}
            </div>
          )}
        </div>
      </Layout>
    );
  }

  const { kpi, recent } = data;

  return (
    <Layout withFooter={false}>
      <div className="max-w-7xl mx-auto px-6 lg:px-8 py-8" data-testid="admin-home">
        <span className="text-xs tracking-[0.2em] uppercase text-[#D6202C] font-medium">Console Admin</span>
        <h1 className="font-heading text-4xl md:text-5xl font-medium tracking-tight mt-2 mb-2">
          Bonjour, {user.name.split(" ")[0]}.
        </h1>
        <p className="text-muted-foreground mb-8">Pilotez l'ensemble de la marketplace 3aqarate.</p>

        {/* KPIs principaux */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8" data-testid="admin-home-kpi">
          <KpiCard
            color="#11783A"
            icon={<Users className="w-5 h-5" />}
            label="Utilisateurs"
            value={kpi.users.total}
            sub={`+${kpi.users.today} aujourd'hui`}
            href="/admin/users"
          />
          <KpiCard
            color="#D6202C"
            icon={<Home className="w-5 h-5" />}
            label="Annonces actives"
            value={kpi.listings.active}
            sub={`+${kpi.listings.today} aujourd'hui · ${kpi.listings.boosted} boostées`}
            href="/search"
          />
          <KpiCard
            color="#F4B400"
            icon={<ShieldAlert className="w-5 h-5" />}
            label="Signalements"
            value={kpi.queue.reports_pending}
            sub="en attente d'examen"
            href="/admin/moderation"
            badge={kpi.queue.reports_pending > 0}
          />
          <KpiCard
            color="#11783A"
            icon={<ShieldCheck className="w-5 h-5" />}
            label="KYC en attente"
            value={kpi.queue.kyc_pending}
            sub="Pro à vérifier"
            href="/admin/kyc"
            badge={kpi.queue.kyc_pending > 0}
          />
        </div>

        {/* Tools quick access */}
        <h2 className="font-heading text-xl font-medium mb-3">Outils admin</h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 mb-10" data-testid="admin-tools">
          <ToolCard href="/admin/analytics" testid="tool-analytics" icon={<BarChart3 />} title="Analytics" desc="MRR, croissance, top villes" />
          <ToolCard href="/admin/users" testid="tool-users" icon={<Users />} title="Utilisateurs" desc="Search, rôles, ban/unban" />
          <ToolCard href="/admin/moderation" testid="tool-moderation" icon={<ShieldAlert />} title="Modération" desc="Signalements et actions" />
          <ToolCard href="/admin/settings" testid="tool-settings" icon={<Sliders />} title="Paramètres" desc="Tarifs Boost & Pro" />
          <ToolCard href="/admin/kyc" testid="tool-kyc" icon={<ShieldCheck />} title="KYC Pro" desc="Vérification documents" />
          <ToolCard href="/agence" testid="tool-listings" icon={<Home />} title="Marketplace" desc="Voir les annonces actives" external={false} hrefOverride="/search" />
        </div>

        {/* MRR */}
        {Object.keys(kpi.mrr).length > 0 && (
          <div className="bg-gradient-to-br from-[#11783A] to-[#0d5e2d] text-white rounded-3xl p-6 mb-10">
            <div className="flex items-center gap-2 text-white/80 text-sm mb-3">
              <Wallet className="w-4 h-4" /> MRR (30 derniers jours)
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-6">
              {Object.entries(kpi.mrr).map(([cur, amt]) => (
                <div key={cur}>
                  <div className="text-3xl font-heading tracking-tight font-medium">
                    {Number(amt).toLocaleString("fr-FR")} <span className="text-base text-white/60 uppercase">{cur}</span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Activité récente */}
        <div className="grid lg:grid-cols-3 gap-4" data-testid="admin-recent">
          <RecentColumn title="Nouveaux utilisateurs" icon={<Plus className="w-4 h-4" />}>
            {recent.users.length === 0 ? <Empty /> : recent.users.map(u => (
              <RecentRow
                key={u.id}
                primary={u.name}
                secondary={`${u.email} · ${u.role}`}
                meta={`${COUNTRY_FLAGS[u.country] || ""} ${fmtDate(u.created_at)}`}
              />
            ))}
          </RecentColumn>
          <RecentColumn title="Nouvelles annonces" icon={<TrendingUp className="w-4 h-4" />}>
            {recent.listings.length === 0 ? <Empty /> : recent.listings.map(l => (
              <RecentRow
                key={l.id}
                href={`/listings/${l.id}`}
                primary={l.title}
                secondary={`${l.city || ""} · ${l.owner_name || ""}`}
                meta={fmtDate(l.created_at)}
                badge={l.status === "hidden" ? "Cachée" : null}
              />
            ))}
          </RecentColumn>
          <RecentColumn title="Signalements récents" icon={<AlertCircle className="w-4 h-4" />}>
            {recent.reports.length === 0 ? <Empty /> : recent.reports.map(r => (
              <RecentRow
                key={r.id}
                href="/admin/moderation"
                primary={r.listing_title}
                secondary={`${REASON_LABELS[r.reason] || r.reason} · ${r.reporter_name}`}
                meta={fmtDate(r.created_at)}
                badge={r.status === "pending" ? "En attente" : "Traité"}
                badgeColor={r.status === "pending" ? "#F4B400" : "#11783A"}
              />
            ))}
          </RecentColumn>
        </div>
      </div>
    </Layout>
  );
}

const KpiCard = ({ color, icon, label, value, sub, href, badge }) => (
  <Link
    to={href}
    className="bg-white rounded-2xl border border-[#E5E5E5] p-5 shadow-sm hover:border-[#D6202C]/30 hover:shadow-md transition relative"
  >
    {badge && <span className="absolute top-3 right-3 w-2.5 h-2.5 rounded-full bg-[#D6202C] animate-pulse" />}
    <div
      className="w-10 h-10 rounded-xl flex items-center justify-center mb-3"
      style={{ background: `${color}15`, color }}
    >
      {icon}
    </div>
    <div className="text-xs text-muted-foreground">{label}</div>
    <div className="font-heading text-3xl font-medium mt-0.5">{value}</div>
    <div className="text-[11px] text-muted-foreground mt-1">{sub}</div>
  </Link>
);

const ToolCard = ({ href, testid, icon, title, desc, hrefOverride }) => (
  <Link
    to={hrefOverride || href}
    data-testid={testid}
    className="group bg-white rounded-2xl border border-[#E5E5E5] p-4 hover:border-[#D6202C] hover:shadow-md transition flex items-center gap-3"
  >
    <div className="w-10 h-10 rounded-xl bg-[#FEF2F3] text-[#D6202C] flex items-center justify-center shrink-0">
      {icon}
    </div>
    <div className="flex-1 min-w-0">
      <div className="font-heading font-medium leading-tight">{title}</div>
      <div className="text-[11px] text-muted-foreground truncate">{desc}</div>
    </div>
    <ArrowRight className="w-4 h-4 text-muted-foreground group-hover:text-[#D6202C] group-hover:translate-x-0.5 transition" />
  </Link>
);

const RecentColumn = ({ title, icon, children }) => (
  <div className="bg-white rounded-2xl border border-[#E5E5E5] p-4">
    <div className="flex items-center gap-2 text-sm font-medium mb-3 text-muted-foreground">
      {icon} {title}
    </div>
    <div className="space-y-2">{children}</div>
  </div>
);

const Empty = () => <div className="text-xs text-muted-foreground italic py-2">Aucune activité récente</div>;

const RecentRow = ({ href, primary, secondary, meta, badge, badgeColor = "#9a9a9a" }) => {
  const Wrap = href ? Link : "div";
  const wrapProps = href ? { to: href } : {};
  return (
    <Wrap {...wrapProps} className={`block p-2.5 rounded-xl ${href ? "hover:bg-[#FAFAFA]" : ""} transition`}>
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0 flex-1">
          <div className="font-medium text-sm truncate">{primary}</div>
          <div className="text-[11px] text-muted-foreground truncate">{secondary}</div>
        </div>
        {badge && (
          <span
            className="text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded-full font-medium shrink-0"
            style={{ background: `${badgeColor}15`, color: badgeColor }}
          >
            {badge}
          </span>
        )}
      </div>
      <div className="text-[10px] text-muted-foreground mt-0.5">{meta}</div>
    </Wrap>
  );
};
